import React, { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft, User, ClipboardList, Stethoscope, Smile,
  AlertTriangle, AlertCircle, Plus, Tags, Edit2,
} from 'lucide-react';
import { fetchWithAuth } from '../utils/api';
import { branchVatRate as branchRate } from '../utils/vatConfig';
import { useLanguage } from './LanguageContext';
import Odontogram from './Odontogram';
import ProgressNoteModal from './ProgressNoteModal';
import DrugLabelPrintModal from './DrugLabelPrintModal';

const PatientRecord = ({ patient, currentBranch, user, onBack }) => {
  const { print_lang } = useLanguage();

  const [activeTab, setActiveTab] = useState('overview');
  const [progressNotes, setProgressNotes] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [dentists, setDentists] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [showNoteModal, setShowNoteModal] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [labelTarget, setLabelTarget] = useState(null);

  const [errorMessage, setErrorMessage] = useState('');

  const branchId = currentBranch?.branch_id;
  const branchVatRateProvided = currentBranch?.vat_rate;
  const [branchVat, setBranchVat] = useState(branchRate(currentBranch));

  const vatFailureMessage = print_lang(
    'vat_rate_load_failed',
    'Could not load the branch VAT rate — prices below may use the wrong tax rate'
  );

  // The caller now threads the whole branch row through, so in practice the
  // rate arrives with the prop and the fetch below never runs. It is kept for
  // the case the rate genuinely cannot be resolved that way — the caller's own
  // branch fetch failed, or the record was opened before it returned — because
  // that fetch is what raises the "prices may use the wrong tax rate" banner.
  // A late-arriving prop wins over whatever this component defaulted to.
  useEffect(() => {
    if (branchVatRateProvided === undefined) return;
    setBranchVat(Number(branchVatRateProvided));
    setErrorMessage((prev) => (prev === vatFailureMessage ? '' : prev));
  }, [branchVatRateProvided, vatFailureMessage]);

  useEffect(() => {
    if (!branchId || branchVatRateProvided !== undefined) return;
    // The dentist JWT carries branch_id but not the branch's VAT rate, and a
    // wrong rate would misprice every line on the note.
    fetchWithAuth(`/api/branches/${branchId}`)
      .then((r) => {
        const b = r?.data || r;
        if (b && b.vat_rate !== undefined) setBranchVat(Number(b.vat_rate));
      })
      .catch(() => {
        // An explicit 0% is a real rate, not a value to guess past. Silently
        // keeping the 7% default here would misprice every line on the note
        // with nothing shown to the dentist, so surface it instead.
        setErrorMessage(vatFailureMessage);
      });
  }, [branchId, branchVatRateProvided, vatFailureMessage]);

  // Full patient fetch (F1): the row threaded in from the queue only carries
  // { patient_id, name, phone, citizen_id, drug_allergy, medical_history } —
  // no DN, age, blood group or prefix. Seed local state with what we already
  // have so the allergy/history banners render immediately and never blank
  // out while the fetch is in flight, then merge in the full record.
  const [patientData, setPatientData] = useState(patient);
  const patientId = patient?.patient_id;

  useEffect(() => {
    setPatientData(patient);
    if (!patientId) return;
    let cancelled = false;
    fetchWithAuth(`/api/patients/${patientId}`)
      .then((r) => {
        if (cancelled) return;
        const full = r?.data || r;
        if (full) setPatientData((prev) => ({ ...prev, ...full }));
      })
      .catch(() => {
        // Keep showing the fields already threaded from the queue row (name,
        // phone, allergy, history) — the DN/age/blood group just stay absent,
        // same as before this fix, rather than the screen going blank.
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  const fetchProgressNotes = useCallback(async () => {
    if (!patient?.patient_id) return;
    try {
      const res = await fetchWithAuth(`/api/progress-notes/patient/${patient.patient_id}`);
      setProgressNotes(res?.data || []);
    } catch {
      setProgressNotes([]);
    }
  }, [patient]);

  useEffect(() => {
    if (!patient?.patient_id) return;
    setIsLoading(true);
    Promise.all([
      fetchProgressNotes(),
      fetchWithAuth(`/api/treatment-records?patient_id=${patient.patient_id}`)
        .then((r) => setTreatments(r?.data || [])).catch(() => setTreatments([])),
      fetchWithAuth(`/api/appointments/dentists/selection?branch_id=${branchId}`)
        .then((r) => setDentists(r?.data || r || [])).catch(() => setDentists([])),
    ]).finally(() => setIsLoading(false));
  }, [patient, branchId, fetchProgressNotes]);

  const openAddNote = () => { setSelectedNote(null); setShowNoteModal(true); };
  const openEditNote = (note) => { setSelectedNote(note); setShowNoteModal(true); };

  const handleFillPrescription = async (prescriptionId) => {
    setErrorMessage('');
    try {
      await fetchWithAuth(`/api/prescriptions/${prescriptionId}/fill`, {
        method: 'PUT', body: JSON.stringify({}),
      });
      fetchProgressNotes();
    } catch (err) {
      setErrorMessage(
        err.code === 'INSUFFICIENT_STOCK'
          ? `${print_lang('insufficient_stock', 'Not enough stock')}: ${err.payload?.medication_name} (${err.payload?.available})`
          : (err.message || print_lang('save_failed', 'Save failed'))
      );
    }
  };

  const handleConfirmSale = async (saleId) => {
    setErrorMessage('');
    try {
      await fetchWithAuth(`/api/shop/sales/${saleId}/confirm`, { method: 'PUT' });
      fetchProgressNotes();
    } catch (err) {
      setErrorMessage(
        err.code === 'INSUFFICIENT_STOCK'
          ? `${print_lang('insufficient_stock', 'Not enough stock')}: ${err.payload?.product_name} (${err.payload?.available})`
          : (err.message || print_lang('save_failed', 'Save failed'))
      );
    }
  };

  const tabs = [
    { id: 'overview',   label: print_lang('overview', 'Overview'),            icon: User },
    { id: 'treatments', label: print_lang('treatments', 'Treatments'),        icon: Stethoscope },
    { id: 'odontogram', label: print_lang('odontogram', 'Odontogram'),        icon: Smile },
    { id: 'notes',      label: print_lang('progress_notes', 'Progress Notes'),icon: ClipboardList },
  ];

  return (
    <div>
      {/* Header. The DN sits beside the name because it is what appears on the
          label the dentist is about to print. */}
      <div className="flex items-center gap-3 mb-4">
        <button onClick={onBack}
                className="p-3 rounded-lg hover:bg-gray-100 active:bg-gray-200"
                title={print_lang('back', 'Back')}>
          <ArrowLeft size={22} />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold flex items-center gap-3">
            {patientData?.prefix}{patientData?.name}
            {patientData?.dn && (
              <span className="font-mono text-sm text-gray-500">{patientData.dn}</span>
            )}
          </h1>
          <div className="text-sm text-gray-500">{patientData?.phone || '-'}</div>
        </div>
      </div>

      {errorMessage && (
        <div className="mb-4 px-3 py-2 bg-red-50 border border-red-300 text-red-700 rounded-lg flex justify-between items-center">
          <span className="text-sm">{errorMessage}</span>
          <button className="min-w-[44px] min-h-[44px] flex items-center justify-center text-red-700"
                  onClick={() => setErrorMessage('')}>&times;</button>
        </div>
      )}

      {(patientData?.drug_allergy || patientData?.medical_history) && (
        <div className="flex flex-wrap gap-2 mb-4">
          {patientData.drug_allergy && (
            <div className="flex items-center gap-2 px-3 py-2 bg-red-100 text-red-700 rounded-lg">
              <AlertTriangle size={18} />
              <div>
                <div className="text-xs font-medium">{print_lang('drug_allergy', 'Drug Allergy')}</div>
                <div className="text-sm">{patientData.drug_allergy}</div>
              </div>
            </div>
          )}
          {patientData.medical_history && (
            <div className="flex items-center gap-2 px-3 py-2 bg-orange-100 text-orange-700 rounded-lg">
              <AlertCircle size={18} />
              <div>
                <div className="text-xs font-medium">{print_lang('medical_history', 'Medical History')}</div>
                <div className="text-sm">{patientData.medical_history}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Touch-sized tabs — py-3 rather than the desktop app's py-2. */}
      <div className="flex gap-1 border-b mb-4 overflow-x-auto">
        {tabs.map((tab) => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-5 py-3 whitespace-nowrap border-b-2 ${
              activeTab === tab.id
                ? 'border-blue-600 text-blue-600 font-medium'
                : 'border-transparent text-gray-600'
            }`}>
            <tab.icon size={18} /> {tab.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-gray-500">{print_lang('loading', 'Loading...')}</div>
      ) : (
        <>
          {activeTab === 'overview' && (
            <div className="grid grid-cols-2 gap-4">
              <div className="border rounded-lg p-4">
                <div className="text-sm text-gray-500">{print_lang('age', 'Age')}</div>
                <div className="text-lg">{patientData?.age ?? '-'}</div>
              </div>
              <div className="border rounded-lg p-4">
                <div className="text-sm text-gray-500">{print_lang('blood_group', 'Blood Group')}</div>
                <div className="text-lg">{patientData?.blood_group || '-'}</div>
              </div>
              <div className="border rounded-lg p-4">
                <div className="text-sm text-gray-500">{print_lang('treatments', 'Treatments')}</div>
                <div className="text-lg font-bold text-blue-600">{treatments.length}</div>
              </div>
              <div className="border rounded-lg p-4">
                <div className="text-sm text-gray-500">{print_lang('progress_notes', 'Progress Notes')}</div>
                <div className="text-lg font-bold text-purple-600">{progressNotes.length}</div>
              </div>
            </div>
          )}

          {activeTab === 'treatments' && (
            treatments.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                {print_lang('no_treatments', 'No treatments recorded')}
              </div>
            ) : (
              <div className="space-y-2">
                {treatments.map((t) => (
                  <div key={t.tmr_id} className="border rounded-lg p-4 flex justify-between">
                    <div>
                      <div className="font-medium">{t.treatment_name}</div>
                      {t.notes && <div className="text-sm text-gray-500">{t.notes}</div>}
                    </div>
                    <div className="text-sm text-gray-500">
                      {t.date ? String(t.date).slice(0, 10) : ''}
                    </div>
                  </div>
                ))}
              </div>
            )
          )}

          {activeTab === 'odontogram' && (
            <Odontogram patientId={patientData.patient_id} currentBranch={currentBranch} />
          )}

          {activeTab === 'notes' && (
            <div>
              <div className="flex justify-end mb-3">
                <button className="px-5 py-3 bg-blue-600 text-white rounded-lg flex items-center gap-2"
                        onClick={openAddNote}>
                  <Plus size={18} /> {print_lang('add_progress_note', 'Add Progress Note')}
                </button>
              </div>

              {progressNotes.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  {print_lang('no_progress_notes', 'No progress notes found')}
                </div>
              ) : (
                <div className="space-y-3">
                  {progressNotes.map((note) => (
                    <div key={note.note_id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <div className="font-medium">
                            {note.visit_date ? String(note.visit_date).slice(0, 10) : ''}
                          </div>
                          {note.dentist_name && (
                            <div className="text-sm text-gray-500">{note.dentist_name}</div>
                          )}
                        </div>
                        <button className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-gray-100"
                                onClick={() => openEditNote(note)}
                                title={print_lang('edit', 'Edit')}>
                          <Edit2 size={16} className="text-blue-600" />
                        </button>
                      </div>

                      {note.chief_complaint && (
                        <div className="mb-1">
                          <span className="text-sm text-gray-500">
                            {print_lang('chief_complaint', 'Chief Complaint')}:
                          </span> {note.chief_complaint}
                        </div>
                      )}
                      {note.diagnosis && (
                        <div className="mb-1">
                          <span className="text-sm text-gray-500">
                            {print_lang('diagnosis', 'Diagnosis')}:
                          </span> {note.diagnosis}
                        </div>
                      )}

                      {note.prescription && (
                        <div className="mt-3 border-t pt-3">
                          <div className="flex justify-between items-center mb-2">
                            <span className="font-medium">
                              {print_lang('medications_prescribed', 'Medications Prescribed')}
                              <span className="ml-2 text-sm text-gray-500">
                                {note.prescription.prescription_number}
                              </span>
                            </span>
                            <span className="badge">
                              {print_lang(`status_${note.prescription.status}`, note.prescription.status)}
                            </span>
                          </div>

                          {(note.prescription.items || []).map((it) => (
                            <div key={it.item_id}
                                 className="flex justify-between items-center text-sm py-2 border-b last:border-0">
                              <span>
                                {it.medication_name}{it.strength ? ` ${it.strength}` : ''}
                                {' × '}{Number(it.quantity_filled) || Number(it.quantity_prescribed)}
                              </span>
                              {/* Only a dispensed line gets a label — a label for
                                  medicine still on the shelf is a lie. */}
                              {it.status === 'filled' && (
                                <button className="min-h-[44px] min-w-[44px] px-3 py-2 text-blue-600 flex items-center gap-1"
                                        onClick={() => setLabelTarget({
                                          prescriptionId: note.prescription.prescription_id, item: it,
                                        })}>
                                  <Tags size={16} /> {print_lang('print_drug_label', 'Print Drug Label')}
                                </button>
                              )}
                            </div>
                          ))}

                          {note.prescription.status === 'pending' && (
                            <button className="mt-2 px-5 py-3 bg-blue-600 text-white rounded-lg"
                                    onClick={() => handleFillPrescription(note.prescription.prescription_id)}>
                              {print_lang('fill_prescription', 'Fill Prescription')}
                            </button>
                          )}
                        </div>
                      )}

                      {note.product_sale && (
                        <div className="mt-3 border-t pt-3">
                          <div className="flex justify-between items-center mb-2">
                            <span className="font-medium">
                              {print_lang('products_sold', 'Products Sold')}
                              <span className="ml-2 text-sm text-gray-500">
                                {note.product_sale.sale_number}
                              </span>
                            </span>
                            <span className="badge">
                              {print_lang(`status_${note.product_sale.status}`, note.product_sale.status)}
                            </span>
                          </div>
                          {note.product_sale.status === 'pending' && (
                            <button className="px-5 py-3 bg-blue-600 text-white rounded-lg"
                                    onClick={() => handleConfirmSale(note.product_sale.sale_id)}>
                              {print_lang('confirm_sale', 'Confirm Sale')}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      <ProgressNoteModal
        isOpen={showNoteModal}
        onClose={() => setShowNoteModal(false)}
        onSaved={() => { setShowNoteModal(false); fetchProgressNotes(); }}
        patient={patientData}
        dentists={dentists}
        branchId={branchId}
        branchVatRate={branchVat}
        note={selectedNote}
        defaultDentistId={user?.dentist_id}
      />

      <DrugLabelPrintModal
        isOpen={!!labelTarget}
        onClose={() => setLabelTarget(null)}
        prescriptionId={labelTarget?.prescriptionId}
        item={labelTarget?.item}
        currentBranch={currentBranch}
      />
    </div>
  );
};

export default PatientRecord;
